import React from "react";
import { useTranslation } from "react-i18next";
import SMCModal from "../../../components/core/SMCModal";
import SMCModalBody from "../../../components/core/SMCModal/SMCModalBody";
import SMCModalButtonContainer from "../../../components/core/SMCModal/SMCModalButtonContainer";
import SMCButton from "../../../components/Simple/SMCButton";
import SMCLabel from "../../../components/Simple/SMCLabel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SMCSelect } from "../../../components/Simple/SMCSelect";
import { fetchFromSMP, possibleMethods } from "../../../api/SMPFetcher";
import { getFirmwareListByVersion } from "../../../api/SMPFetchHelper";

const API_KEY_TO_TYPE = {
  firmware1200List: { type: '1200', text: '1200R Appliance' },
  firmwareS1S2List: { type: 'S1S2', text: '700/1400 Appliance' },
  firmwareV0V1List: { type: 'V0V1', text: '1530/1550/1570/1590/1500R Appliance' },
  firmwareV0V1ProList: { type: 'V0V1Pro', text: '1535/1555/1575/1595 Appliance' },
  firmwareV2V3List: { type: 'V2V3', text: '1600/1800/1900/2000 Appliance' },
  firmwareV1RSlimList: { type: 'V1RSlim', text: '1595R Appliance' },
  firmwareT0List: { type: 'T0', text: '2530/2550 Appliance' },
  firmwareT12T15T3List: { type: 'T12T15T3', text: '2560/2570/2580/2590 Appliance' },
};

function RecommandFirmwareWizard({ isOpen, onRequestClose, onSuccess }) {
  const { t } = useTranslation();
  const [selectedFirmwares, setSelectedFirmwares] = React.useState({});
  const [groupedFirmware, setGroupedFirmware] = React.useState({});

  React.useEffect(() => {
    if (isOpen) {
      getFirmwareListByVersion(false, false)
        .then((res) => {
          if (res && res.data && res.data.data) {
            const data = res.data.data;
            const groups = {};
            Object.keys(API_KEY_TO_TYPE).forEach((key) => {
              const config = API_KEY_TO_TYPE[key];
              if (data[key]) {
                groups[config.type] = [...data[key]].sort((a, b) => {
                  if (a.recommended === b.recommended) return 0;
                  return a.recommended ? -1 : 1;
                });
              }
            });
            setGroupedFirmware(groups);
          }
        })
        .catch((err) => console.error(err));
    }
  }, [isOpen]);

  React.useEffect(() => {
    const initialSelection = {};
    Object.keys(groupedFirmware).forEach((type) => {
      if (groupedFirmware[type].length > 0) {
        const recommended = groupedFirmware[type].find((fw) => fw.recommended);
        initialSelection[type] = recommended ? recommended.uid : groupedFirmware[type][0].uid;
      }
    });
    setSelectedFirmwares(initialSelection);
  }, [groupedFirmware]);

  const handleSave = () => {
    const payload = {};
    Object.keys(selectedFirmwares).forEach((type) => {
      const uid = selectedFirmwares[type];
      const group = groupedFirmware[type];
      const fw = group ? group.find((f) => f.uid === uid) : null;
      if (fw) {
        payload[type] = fw.uid;
      }
    });

    fetchFromSMP("/system/firmware/set-recommended-list", possibleMethods.POST, { data: payload })
      .then(() => {
        if (onSuccess) onSuccess();
        onRequestClose();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return (
    <SMCModal
      title={'Recommend Firmware Versions'}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      width={850}
      height={755}
    >
      <SMCModalBody>
          <SMCLabel>{t("SMC_FIRMWARE_TYPE_TEXT")}</SMCLabel>
          {Object.values(API_KEY_TO_TYPE).map((config) => {
            const firmwares = groupedFirmware[config.type];
            if (!firmwares) return null;

            return (
              <div key={config.type} style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <SMCLabel style={{ marginRight: 10 }}>{config.text}</SMCLabel>
                <SMCSelect
                  value={selectedFirmwares[config.type] || ""}
                  onChange={(e) =>
                    setSelectedFirmwares({ ...selectedFirmwares, [config.type]: e.target.value })
                  }
                  style={{ width: "300px", marginRight: "20px" }}
                >
                  {firmwares.map((fw) => (
                    <option key={fw.uid} value={fw.uid}>
                      {fw.name}
                    </option>
                  ))}
                </SMCSelect>
              </div>
            );
          })}
      </SMCModalBody>
      <SMCModalButtonContainer>
        <SMCButton onClick={handleSave} style={{ marginRight: 5 }}>
          {t("SMC_GEN_SAVE")}
          <FontAwesomeIcon
            style={{ marginTop: "3px", marginLeft: "5px" }}
            color="grey"
            icon="save"
          />
        </SMCButton>
        <SMCButton onClick={onRequestClose}>
          <FontAwesomeIcon
            style={{ marginTop: "3px", marginRight: "5px" }}
            color="grey"
            icon="times"
          />
          {t("SMC_GEN_CANCEL")}
        </SMCButton>
      </SMCModalButtonContainer>
    </SMCModal>
  );
}

export default RecommandFirmwareWizard;
